import { useQuery } from '@tanstack/react-query';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import type { TaskCategory } from '../projects/data';
import type { Priority } from '../projects/requests-data';

// Datos de /api/tareas (guía 7.3).

export interface RelatedTask {
  id: string;
  title: string;
  status: string;
  statusCategory: TaskCategory;
  dueDate: string | null;
  sortOrder: number;
}

export interface TaskDetail {
  id: string;
  organizationId: string;
  projectId: string | null;
  parentId: string | null;
  title: string;
  description: string | null;
  statusId: string;
  status: string;
  statusCategory: TaskCategory;
  priority: Priority;
  startDate: string | null;
  dueDate: string | null;
  estimateHours: number | null;
  recurrenceRule: string | null;
  recurrenceOfId: string | null;
  completedAt: string | null;
  lastActivityAt: string;
  createdBy: string | null;
  createdByName: string | null;
  overdue: boolean;
  project: { id: string; number: number; code: string; name: string } | null;
  assignees: Array<{ id: string; fullName: string | null }>;
  labels: Array<{ id: string; name: string; color: string }>;
  blockedBy: RelatedTask[];
  blocks: RelatedTask[];
  subtasks: RelatedTask[];
  comments: Array<{ id: string; authorId: string; author: string | null; body: string; createdAt: string }>;
  time: Array<{ id: string; userId: string; user: string | null; spentOn: string; minutes: number; note: string | null }>;
  totalMinutes: number;
  history: Array<{ status: string; category: TaskCategory; changedAt: string; changedBy: string | null; changedByName: string | null }>;
  can: { view: boolean; edit: boolean; work: boolean };
}

export const useTask = (id: string) => useQuery({ queryKey: ['tareas', 'detalle', id], queryFn: () => api<TaskDetail>(`/api/tareas/${id}`) });

// --- Recurrencia: el mismo subconjunto de RRULE que la API (FREQ, INTERVAL y BYDAY con WEEKLY) ---

export type Frequency = 'none' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export interface RecurrenceForm {
  freq: Frequency;
  interval: number;
  days: Weekday[];
}

export function buildRule(f: RecurrenceForm): string | null {
  if (f.freq === 'none') return null;
  const parts = [`FREQ=${f.freq}`];
  if (f.interval > 1) parts.push(`INTERVAL=${f.interval}`);
  if (f.freq === 'WEEKLY' && f.days.length > 0) parts.push(`BYDAY=${WEEKDAYS.filter((d) => f.days.includes(d)).join(',')}`);
  return parts.join(';');
}

export function parseRule(rule: string | null): RecurrenceForm {
  const empty: RecurrenceForm = { freq: 'none', interval: 1, days: [] };
  if (!rule) return empty;
  const parts = new Map(rule.toUpperCase().split(';').map((p) => p.split('=') as [string, string]));
  const freq = parts.get('FREQ') as Frequency | undefined;
  if (!freq || !['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(freq)) return empty;
  return {
    freq,
    interval: Number(parts.get('INTERVAL') ?? 1) || 1,
    days: (parts.get('BYDAY')?.split(',') ?? []).filter((d): d is Weekday => (WEEKDAYS as readonly string[]).includes(d)),
  };
}

/** La regla en palabras: "Cada semana los lunes y viernes", "Cada 3 meses". */
export function describeRule(rule: string | null): string | null {
  const f = parseRule(rule);
  if (f.freq === 'none') return null;
  const unit = { DAILY: ['día', 'días'], WEEKLY: ['semana', 'semanas'], MONTHLY: ['mes', 'meses'], YEARLY: ['año', 'años'] }[f.freq];
  const every = f.interval === 1 ? t('ta.cada_uno', { unidad: unit[0]! }) : t('ta.cada_n', { n: f.interval, unidad: unit[1]! });
  if (f.freq !== 'WEEKLY' || f.days.length === 0) return every;
  const names = f.days.map((d) => t(`ta.dia_${d}` as 'ta.dia_MO'));
  const list = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} y ${names.at(-1)}`;
  return t('ta.cada_dias', { cada: every, dias: list });
}

export const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
};

/** Una tarea de la lista de /api/tareas (propias, asignadas o de un proyecto). */
export interface TaskListItem {
  id: string;
  title: string;
  projectId: string | null;
  projectNumber: number | null;
  projectName: string | null;
  projectCode: string | null;
  parentId: string | null;
  statusId: string;
  status: string;
  statusCategory: TaskCategory;
  priority: Priority;
  startDate: string | null;
  dueDate: string | null;
  sortOrder: number;
  recurrenceRule: string | null;
  lastActivityAt: string;
  overdue: boolean;
  assignees: Array<{ id: string; fullName: string | null }>;
}

// --- Resumen semanal y mensual (GET /api/tareas/resumen) ---

export type Period = 'semanal' | 'mensual';

export interface SummaryTask {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  completedOn: string | null;
  project: { number: number; code: string; name: string | null } | null;
}

export interface SummaryTicket {
  number: number;
  code: string;
  title: string;
  status: string;
  resolvedOn: string | null;
  slaDueAt: string | null;
}

export interface Summary {
  period: Period;
  label: string;
  start: string;
  end: string;
  nextStart: string;
  nextEnd: string;
  scope: { type: 'personal' } | { type: 'proyecto'; id: string; number: number; code: string; name: string };
  done: { tasks: SummaryTask[]; tickets: SummaryTicket[] };
  inProgress: { tasks: SummaryTask[]; tickets: SummaryTicket[] };
  upcoming: { tasks: SummaryTask[]; tickets: SummaryTicket[] };
  attention: { tasks: SummaryTask[]; tickets: SummaryTicket[] };
  closing: string;
}
